import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PushPin as PushPinIcon,
    PushPinOutlined as PushPinOutlinedIcon,
    Event as EventIcon,
    Campaign as CampaignIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { useAuth } from '../../context/AuthContext';
import type { Nastenka } from '../../api/JsdhNotifikaceService/generated/models/Nastenka';
import {
    useNastenkaFeedQuery,
    useCreateNastenkaMutation,
    useUpdateNastenkaMutation,
    useDeleteNastenkaMutation,
    usePinNastenkaMutation,
} from '../../api/JsdhNotifikaceService/service/NastenkaMutations';

const formatDateTime = (value?: string): string => {
    if (!value) return '';
    return new Date(value).toLocaleString('cs-CZ', {
        day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

interface FormState {
    nadpis: string;
    popis: string;
    datumUdalosti: string;   // datetime-local input value ('' = bez data)
    poslatNotifikaci: boolean;
}

const EMPTY_FORM: FormState = { nadpis: '', popis: '', datumUdalosti: '', poslatNotifikaci: false };

const NastenkaPage: React.FC = () => {
    const { user, isAdmin } = useAuth();
    const { data: feed, isLoading, error } = useNastenkaFeedQuery();
    const createMutation = useCreateNastenkaMutation();
    const updateMutation = useUpdateNastenkaMutation();
    const deleteMutation = useDeleteNastenkaMutation();
    const pinMutation = usePinNastenkaMutation();

    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selected, setSelected] = useState<Nastenka | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);

    const canPost = feed?.canPost ?? false;
    const canEdit = (p: Nastenka) => isAdmin || p.vytvorilId === user?.id;

    const handleOpenCreate = () => {
        setSelected(null);
        setForm(EMPTY_FORM);
        setOpenDialog(true);
    };

    const handleOpenEdit = (p: Nastenka) => {
        setSelected(p);
        setForm({
            nadpis: p.nadpis,
            popis: p.popis || '',
            // BE serializuje LocalDateTime bez timezone (yyyy-MM-ddTHH:mm:ss) -> slice(0,16)
            // je bezpecny pro datetime-local input; new Date() ho parsuje jako lokalni cas.
            datumUdalosti: p.datumUdalosti ? p.datumUdalosti.slice(0, 16) : '',
            poslatNotifikaci: false,
        });
        setOpenDialog(true);
    };

    const handleSubmit = () => {
        const datumUdalosti = form.datumUdalosti ? form.datumUdalosti : null;
        if (selected) {
            updateMutation.mutate(
                { id: selected.id, data: { nadpis: form.nadpis.trim(), popis: form.popis, datumUdalosti } },
                { onSuccess: () => setOpenDialog(false) }
            );
        } else {
            createMutation.mutate(
                { nadpis: form.nadpis.trim(), popis: form.popis, datumUdalosti, poslatNotifikaci: form.poslatNotifikaci },
                { onSuccess: () => setOpenDialog(false) }
            );
        }
    };

    const handleDelete = () => {
        if (selected) {
            deleteMutation.mutate(selected.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelected(null);
                },
            });
        }
    };

    if (error) {
        return <Alert severity="error">Chyba při načítání nástěnky: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Nástěnka"
                action={
                    canPost ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                            Přidat příspěvek
                        </Button>
                    ) : undefined
                }
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !feed || feed.prispevky.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <CampaignIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Nástěnka je prázdná
                    </Typography>
                    {canPost && (
                        <Typography variant="body2" color="text.secondary">
                            Přidejte první příspěvek tlačítkem výše.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {feed.prispevky.map((p) => (
                        <Card
                            key={p.id}
                            variant="outlined"
                            sx={p.pripnuto ? { borderColor: 'primary.main', borderWidth: 2 } : undefined}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        {p.pripnuto && (
                                            <PushPinIcon fontSize="small" color="primary" />
                                        )}
                                        <Typography variant="h6" fontWeight={700}>
                                            {p.nadpis}
                                        </Typography>
                                        {p.datumUdalosti && (
                                            <Chip
                                                icon={<EventIcon />}
                                                label={formatDateTime(p.datumUdalosti)}
                                                color="secondary"
                                                size="small"
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                        {isAdmin && (
                                            <Tooltip title={p.pripnuto ? 'Odepnout' : 'Připnout'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => pinMutation.mutate({ id: p.id, pripnout: !p.pripnuto })}
                                                >
                                                    {p.pripnuto
                                                        ? <PushPinIcon fontSize="small" color="primary" />
                                                        : <PushPinOutlinedIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canEdit(p) && (
                                            <>
                                                <Tooltip title="Upravit">
                                                    <IconButton size="small" onClick={() => handleOpenEdit(p)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Smazat">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => { setSelected(p); setOpenDeleteDialog(true); }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                {p.popis && (
                                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                                        {p.popis}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {p.autorJmeno} · {formatDateTime(p.createdAt)}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selected ? 'Upravit příspěvek' : 'Nový příspěvek'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Nadpis"
                            value={form.nadpis}
                            onChange={(e) => setForm({ ...form, nadpis: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 200 }}
                        />
                        <TextField
                            label="Text příspěvku"
                            value={form.popis}
                            onChange={(e) => setForm({ ...form, popis: e.target.value })}
                            fullWidth
                            multiline
                            minRows={4}
                        />
                        <TextField
                            label="Datum události (volitelné)"
                            type="datetime-local"
                            value={form.datumUdalosti}
                            onChange={(e) => setForm({ ...form, datumUdalosti: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            helperText="Vyplňte jen pokud se příspěvek týká konkrétního termínu — zobrazí se s datem"
                        />
                        {!selected && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.poslatNotifikaci}
                                        onChange={(e) => setForm({ ...form, poslatNotifikaci: e.target.checked })}
                                    />
                                }
                                label="Poslat push notifikaci členům"
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!form.nadpis.trim() || createMutation.isPending || updateMutation.isPending}
                    >
                        {(createMutation.isPending || updateMutation.isPending) && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {selected ? 'Uložit' : 'Zveřejnit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat příspěvek?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat příspěvek <strong>{selected?.nadpis}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NastenkaPage;
